import React from "react";
import Card from "components/card";
import {
  MdCheckCircle,
  MdCancel,
  MdLock,
  MdVisibility,
  MdFace,
  MdBlock,
  MdCheckBox,
  MdDelete,
  MdMoreVert,
  MdSearch,
  MdAdd,
  MdClose,
  MdBadge,
} from "react-icons/md";
import type { UserListItem, UserRole, CenterListItem } from "types/domain";
import FilterChip from "components/ui/FilterChip";
import BulkSelectBar from "components/ui/BulkSelectBar";
import { useLanguage } from "context/LanguageContext";

const ROLE_LABEL_KEYS: Record<UserRole, string> = {
  SYSTEM_ADMIN: "enum.role.SYSTEM_ADMIN",
  MANAGER: "enum.role.MANAGER",
  TRAINER: "enum.roleShort.TRAINER",
  TEACHER: "enum.role.TEACHER",
  STUDENT: "enum.role.STUDENT",
};

interface UserTableProps {
  data: UserListItem[];
  totalUsers: number;
  loading: boolean;
  onCreateClick: () => void;
  selectedRole: UserRole | "ALL";
  onRoleChange: (role: UserRole | "ALL") => void;
  selectedCenterId: string | "ALL";
  onCenterChange: (centerId: string | "ALL") => void;
  centers: CenterListItem[];
  centersLoading: boolean;
  activeFilter: "all" | "active" | "inactive";
  onActiveFilterChange: (value: "all" | "active" | "inactive") => void;
  onSetPassword?: (userId: string) => void;
  onViewProfile?: (user: UserListItem) => void;
  onToggleActive?: (userId: string, currentlyActive: boolean) => void;
  onDeleteUser?: (userId: string) => void;
  onBulkDeleteUsers?: (userIds: string[]) => void;
  currentUserRole?: UserRole | null;
  onUserRoleChange?: (userId: string, newRole: string) => void;
  roleChangeResetToken?: number;
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
}

// Debounce window for forwarding keystrokes from the search box to the
// URL / network — keeps the table from refetching on every character.
const SEARCH_DEBOUNCE_MS = 300;

// ── Role select cell with pending state so there's no DOM snap-back (C3) ──
interface RoleSelectCellProps {
  role: UserRole;
  user: UserListItem;
  currentUserRole: UserRole | null | undefined;
  onUserRoleChange: (userId: string, newRole: string) => void;
  resetToken: number;
}

function RoleSelectCell({
  role,
  user,
  currentUserRole,
  onUserRoleChange,
  resetToken,
}: RoleSelectCellProps) {
  const { t } = useLanguage();
  const [pendingRole, setPendingRole] = React.useState<string | null>(null);

  // Reset pending selection when the modal is cancelled (resetToken bumps) or
  // when the confirmed role arrives as a new prop.
  React.useEffect(() => {
    setPendingRole(null);
  }, [resetToken, role]);

  const manageableRoles: UserRole[] = ["STUDENT", "TRAINER", "TEACHER"];
  const availableRoles: UserRole[] =
    currentUserRole === "SYSTEM_ADMIN"
      ? ["STUDENT", "TRAINER", "TEACHER", "MANAGER"]
      : manageableRoles;

  return (
    <select
      value={pendingRole ?? role ?? ""}
      onChange={(e) => {
        const newRole = e.target.value;
        if (newRole === role) return;
        setPendingRole(newRole);
        onUserRoleChange(user.id, newRole);
      }}
      className="rounded-lg border border-line bg-brand-50 px-2 py-1 text-xs font-medium text-brand-700 outline-none dark:bg-brand-900/30 dark:text-brand-400"
    >
      {availableRoles.map((r) => (
        <option key={r} value={r}>
          {t(ROLE_LABEL_KEYS[r])}
        </option>
      ))}
    </select>
  );
}

// ── Kebab action menu for secondary row actions (H4) ──
interface ActionMenuProps {
  user: UserListItem;
  canSetPassword: boolean;
  canToggle: boolean;
  onSetPassword?: (userId: string) => void;
  onToggleActive?: (userId: string, currentlyActive: boolean) => void;
}

function ActionMenu({
  user,
  canSetPassword,
  canToggle,
  onSetPassword,
  onToggleActive,
}: ActionMenuProps) {
  const { t } = useLanguage();
  const [open, setOpen] = React.useState(false);
  const [coords, setCoords] = React.useState({ top: 0, right: 0 });
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const hasSecondary = canSetPassword || canToggle;
  if (!hasSecondary) return null;

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen((v) => !v);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        aria-label={t("usersMgmt.table.moreActions")}
        className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg border border-line text-ink-muted hover:bg-surface-hover"
      >
        <MdMoreVert className="h-4 w-4" />
      </button>

      {open && (
        <div
          ref={menuRef}
          style={{ position: "fixed", top: coords.top, right: coords.right }}
          className="z-[9999] min-w-[160px] rounded-xl border border-line bg-surface py-1 shadow-lg"
        >
          {canSetPassword && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onSetPassword?.(user.id);
              }}
              className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs font-medium text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-surface-hover"
            >
              <MdLock className="h-3.5 w-3.5 shrink-0" />
              {t("usersMgmt.table.setPassword")}
            </button>
          )}
          {canToggle && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onToggleActive?.(user.id, user.is_active);
              }}
              className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs font-medium hover:bg-surface-hover ${ user.is_active ? "text-orange-600 dark:text-orange-400"
                  : "text-green-700 dark:text-green-400"
              }`}
            >
              {user.is_active ? (
                <>
                  <MdBlock className="h-3.5 w-3.5 shrink-0" />{" "}
                  {t("usersMgmt.table.deactivate")}
                </>
              ) : (
                <>
                  <MdCheckBox className="h-3.5 w-3.5 shrink-0" />{" "}
                  {t("usersMgmt.table.activate")}
                </>
              )}
            </button>
          )}
        </div>
      )}
    </>
  );
}

function UserTable({
  data,
  totalUsers,
  loading,
  onCreateClick,
  selectedRole,
  onRoleChange,
  selectedCenterId,
  onCenterChange,
  centers,
  centersLoading,
  activeFilter,
  onActiveFilterChange,
  onSetPassword,
  onViewProfile,
  onToggleActive,
  onDeleteUser,
  onBulkDeleteUsers,
  currentUserRole,
  onUserRoleChange,
  roleChangeResetToken = 0,
  searchQuery = "",
  onSearchChange,
}: UserTableProps) {
  const { t, locale } = useLanguage();
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());

  const toggleSelected = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // Local mirror of the search box so each keystroke does not push a new
  // URL / re-fetch; we forward to ``onSearchChange`` only after the user
  // stops typing for ``SEARCH_DEBOUNCE_MS``.
  const [localSearch, setLocalSearch] = React.useState(searchQuery);
  React.useEffect(() => {
    setLocalSearch(searchQuery);
  }, [searchQuery]);
  React.useEffect(() => {
    if (localSearch === searchQuery) return;
    const timer = window.setTimeout(() => {
      onSearchChange?.(localSearch);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [localSearch, searchQuery, onSearchChange]);

  // C2: Trust the server — use safeData directly, no client-side re-filtering.
  // Vietnamese full names are written family-middle-given; the user's mental
  // model of "A → Z" sorts by the *last* word (the given name).
  const safeData = React.useMemo(() => {
    const items = Array.isArray(data) ? data : [];
    const lastWord = (fullName: string | null | undefined) => {
      const parts = String(fullName ?? "")
        .trim()
        .split(/\s+/);
      return (parts[parts.length - 1] ?? "").toLocaleLowerCase("vi-VN");
    };
    return [...items].sort((a, b) =>
      lastWord(a.full_name).localeCompare(lastWord(b.full_name), "vi")
    );
  }, [data]);

  // Drop any selected id that no longer appears in the current (filtered) list.
  React.useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set(safeData.map((u) => u.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [safeData]);

  const centerOptions = React.useMemo(() => {
    return centers
      .map((center) => ({ id: center.id, name: center.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [centers]);

  const allRoleFilterOptions: Array<{
    value: UserRole | "ALL";
    label: string;
  }> = [
    { value: "ALL", label: t("usersMgmt.table.allRoles") },
    { value: "MANAGER", label: t("enum.role.MANAGER") },
    { value: "TRAINER", label: t("enum.roleShort.TRAINER") },
    { value: "TEACHER", label: t("enum.role.TEACHER") },
    { value: "STUDENT", label: t("enum.role.STUDENT") },
  ];
  const roleFilterOptions =
    currentUserRole === "TEACHER" || currentUserRole === "TRAINER"
      ? allRoleFilterOptions.filter((o) => o.value !== "MANAGER")
      : allRoleFilterOptions;

  const statusFilterOptions: Array<{
    value: "all" | "active" | "inactive";
    label: string;
  }> = [
    { value: "all", label: t("usersMgmt.table.allStatuses") },
    { value: "active", label: t("usersMgmt.table.filterActive") },
    { value: "inactive", label: t("usersMgmt.table.filterInactive") },
  ];

  // M2: context-aware empty state message
  const hasActiveFilters =
    selectedRole !== "ALL" ||
    selectedCenterId !== "ALL" ||
    activeFilter !== "all" ||
    searchQuery.trim().length > 0;

  // Split around the placeholder so the count can stay bold without
  // concatenating separately translated fragments.
  const [footerBefore = "", footerAfter = ""] = t(
    "usersMgmt.table.footerShowing"
  ).split("{count}");

  const clearAllFilters = () => {
    onRoleChange("ALL");
    onCenterChange("ALL");
    onActiveFilterChange("all");
    onSearchChange?.("");
  };

  return (
    <Card
      extra={
        "flex h-[calc(100vh-112px)] min-h-0 w-full flex-col px-5 pb-5 sm:px-6"
      }
    >
      <div className="pt-5">
        <div className="flex flex-col gap-4 border-b border-line pb-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-xl font-bold text-ink">
              {t("usersMgmt.table.title", {
                count: totalUsers.toLocaleString(locale),
              })}
            </div>
            <p className="mt-1 text-sm text-ink-muted">
              {t("usersMgmt.table.subtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={onCreateClick}
            className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 active:bg-brand-700 sm:w-auto"
          >
            <MdAdd className="h-5 w-5" />
            {t("usersMgmt.create.submit")}
          </button>
        </div>

        <div className="mt-4 flex flex-col flex-wrap gap-2 rounded-xl border border-line bg-gray-50/70 p-3 dark:bg-surface/50 xl:flex-row xl:items-center">
          {onSearchChange && (
            <div className="flex h-10 min-w-[280px] flex-1 items-center gap-2 rounded-lg border border-line bg-surface px-3 focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500 xl:max-w-[520px]">
              <MdSearch className="h-4 w-4 shrink-0 text-ink-subtle" />
              <input
                type="text"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                placeholder={t("usersMgmt.table.searchPlaceholder")}
                className="bg-transparent min-w-0 flex-1 text-sm text-ink placeholder-gray-400 outline-none dark:placeholder-gray-500"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => onSearchChange("")}
                  aria-label={t("usersMgmt.table.clearSearch")}
                  className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-ink-subtle hover:bg-surface-hover hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <MdClose className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {roleFilterOptions.map((option) => (
              <FilterChip
                key={option.value}
                active={selectedRole === option.value}
                onClick={() => onRoleChange(option.value)}
              >
                {option.label}
              </FilterChip>
            ))}
          </div>

          <div className="grid flex-1 gap-2 sm:grid-cols-1 xl:flex xl:flex-none xl:items-center">
            {currentUserRole === "SYSTEM_ADMIN" && (
              <label className="flex h-10 items-center gap-2 rounded-lg border border-line bg-surface pl-3 xl:w-[260px]">
                <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  {t("usersMgmt.create.center")}
                </span>
                {centersLoading ? (
                  <div className="flex h-full min-w-0 flex-1 items-center gap-2 px-2">
                    <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-brand-500" />
                    <span className="text-sm text-ink-muted">
                      {t("state.loading")}
                    </span>
                  </div>
                ) : (
                  <select
                    value={selectedCenterId}
                    onChange={(event) => onCenterChange(event.target.value)}
                    className="bg-transparent h-full min-w-0 flex-1 rounded-lg px-2 text-sm font-medium text-ink outline-none"
                  >
                    <option value="ALL">
                      {t("usersMgmt.table.allCenters")}
                    </option>
                    {centerOptions.map((center) => (
                      <option key={center.id} value={center.id}>
                        {center.name}
                      </option>
                    ))}
                  </select>
                )}
              </label>
            )}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="mr-1 text-xs font-bold uppercase tracking-wide text-ink-subtle">
            {t("usersMgmt.table.status")}
          </span>
          {statusFilterOptions.map((option) => (
            <FilterChip
              key={option.value}
              active={activeFilter === option.value}
              onClick={() => onActiveFilterChange(option.value)}
            >
              {option.label}
            </FilterChip>
          ))}
        </div>
      </div>

      {/* Table — capped height + internal scroll so the page never grows a scrollbar */}
      <div className="mt-4 flex min-h-0 flex-1 flex-col">
        {loading ? (
          <div className="flex-1 space-y-3 py-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="h-10 w-full animate-pulse rounded-lg bg-gray-200 dark:bg-surface-muted"
              />
            ))}
          </div>
        ) : safeData.length === 0 ? (
          // M2: context-aware empty state
          <div className="py-12 text-center">
            <p className="text-sm font-medium text-ink-muted">
              {hasActiveFilters
                ? t("usersMgmt.table.emptyFiltered")
                : t("usersMgmt.table.empty")}
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="mt-3 cursor-pointer text-sm font-medium text-brand-500 hover:underline"
              >
                {t("action.clearFilters")}
              </button>
            )}
          </div>
        ) : (
          <div className="flex h-full min-h-0 flex-col">
            {selectedIds.size > 0 ? (
              <BulkSelectBar
                selectedCount={selectedIds.size}
                onClear={() => setSelectedIds(new Set())}
                onDelete={
                  onBulkDeleteUsers
                    ? () => onBulkDeleteUsers(Array.from(selectedIds))
                    : undefined
                }
              />
            ) : (
              <label className="mb-3 flex shrink-0 cursor-pointer items-center gap-2 px-1">
                <input
                  type="checkbox"
                  checked={
                    safeData.length > 0 && selectedIds.size === safeData.length
                  }
                  onChange={() =>
                    setSelectedIds(
                      selectedIds.size === safeData.length
                        ? new Set()
                        : new Set(safeData.map((u) => u.id))
                    )
                  }
                  className="h-4 w-4 rounded border-line text-brand-500 focus:ring-brand-400"
                />
                <span className="text-xs font-medium text-ink-subtle">
                  {t("usersMgmt.table.selectAll", { count: safeData.length })}
                </span>
              </label>
            )}
            <div className="min-h-0 flex-1 overflow-auto pr-1">
              <div className="flex flex-col gap-2.5">
                {safeData.map((user) => {
                  const canToggle =
                    currentUserRole === "MANAGER" ||
                    currentUserRole === "SYSTEM_ADMIN"
                      ? true
                      : currentUserRole === "TEACHER"
                      ? user.role === "STUDENT" || user.role === "TRAINER"
                      : false;
                  const canSetPassword = canToggle;

                  const role = user.role as UserRole | null | undefined;
                  const roleLabel = role
                    ? ROLE_LABEL_KEYS[role]
                      ? t(ROLE_LABEL_KEYS[role])
                      : role
                    : t("enum.role.unknown");
                  const manageableRoles: UserRole[] = [
                    "STUDENT",
                    "TRAINER",
                    "TEACHER",
                  ];
                  const canChangeRole =
                    onUserRoleChange &&
                    (((currentUserRole === "MANAGER" ||
                      currentUserRole === "TEACHER") &&
                      role != null &&
                      manageableRoles.includes(role)) ||
                      (currentUserRole === "SYSTEM_ADMIN" &&
                        role != null &&
                        role !== "SYSTEM_ADMIN"));

                  return (
                    <div
                      key={user.id}
                      className="flex flex-col gap-3 rounded-2xl border border-line bg-surface p-4 transition-colors hover:bg-gray-50/70 dark:hover:bg-surface-hover/40 sm:flex-row sm:items-center sm:gap-4"
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(user.id)}
                        onChange={() => toggleSelected(user.id)}
                        className="h-4 w-4 shrink-0 rounded border-line text-brand-500 focus:ring-brand-400"
                      />

                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-sm font-bold uppercase text-brand-600 dark:bg-brand-900/30 dark:text-brand-300">
                        {(user.full_name || "?").trim().charAt(0)}
                      </span>

                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-1.5 truncate text-sm font-bold text-ink">
                          {user.full_name || t("usersMgmt.table.noName")}
                          {user.is_first_login && (
                            <span
                              className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400"
                              title={t("usersMgmt.table.notFirstLoggedIn")}
                            />
                          )}
                        </p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-ink-muted">
                          <span className="truncate">
                            {user.email ?? t("usersMgmt.table.noEmail")}
                          </span>
                          {(user.username || user.code) && (
                            <span className="inline-flex items-center gap-1 whitespace-nowrap">
                              <MdBadge className="h-3.5 w-3.5 shrink-0" />
                              {user.username ?? user.code}
                            </span>
                          )}
                          {user.updated_at && (
                            <span className="whitespace-nowrap text-ink-subtle">
                              {t("usersMgmt.table.updatedAt", {
                                time: new Date(user.updated_at).toLocaleString(
                                  locale,
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                ),
                              })}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                        <div className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-surface-muted px-2.5 py-1">
                          <MdFace
                            className={`h-4 w-4 shrink-0 ${
                              (user.face_count ?? 0) > 0
                                ? "text-green-500"
                                : "text-ink-subtle"
                            }`}
                          />
                          <span
                            className={`text-xs font-medium ${
                              (user.face_count ?? 0) > 0
                                ? "text-navy-700 dark:text-white"
                                : "text-ink-subtle"
                            }`}
                          >
                            {user.face_count ?? 0}
                          </span>
                        </div>

                        {canChangeRole && onUserRoleChange && role != null ? (
                          <RoleSelectCell
                            role={role}
                            user={user}
                            currentUserRole={currentUserRole}
                            onUserRoleChange={onUserRoleChange}
                            resetToken={roleChangeResetToken}
                          />
                        ) : (
                          <span className="inline-block whitespace-nowrap rounded-full bg-brand-100 px-3 py-1 text-xs font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-400">
                            {roleLabel}
                          </span>
                        )}

                        {user.is_active ? (
                          <div className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-green-50 px-2.5 py-1 dark:bg-green-900/20">
                            <MdCheckCircle className="h-4 w-4 shrink-0 text-green-500" />
                            <p className="text-xs font-semibold text-green-600 dark:text-green-400">
                              {t("usersMgmt.table.statusActive")}
                            </p>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-red-50 px-2.5 py-1 dark:bg-red-900/20">
                            <MdCancel className="h-4 w-4 shrink-0 text-red-500" />
                            <p className="text-xs font-semibold text-red-600 dark:text-red-400">
                              {t("usersMgmt.table.statusInactive")}
                            </p>
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() => onViewProfile?.(user)}
                          className="inline-flex cursor-pointer items-center gap-1 rounded-lg bg-brand-50 px-3 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-100 dark:bg-brand-900/20 dark:text-brand-300 dark:hover:bg-brand-900/35"
                          title={t("usersMgmt.table.viewProfile")}
                        >
                          <MdVisibility className="h-3.5 w-3.5" />
                          {t("usersMgmt.table.viewProfile")}
                        </button>
                        {canToggle &&
                          (user.can_delete === false ? (
                            <button
                              type="button"
                              disabled
                              title={t("usersMgmt.table.deleteForbidden")}
                              className="flex h-9 w-9 shrink-0 cursor-not-allowed items-center justify-center rounded-xl border border-line bg-surface-muted text-gray-300 dark:text-gray-500"
                            >
                              <MdDelete className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => onDeleteUser?.(user.id)}
                              title={t("usersMgmt.table.deleteUser")}
                              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-danger-100 bg-danger-50 text-danger-600 hover:bg-danger-100 dark:border-danger-900/30 dark:bg-danger-900/10 dark:text-danger-400 dark:hover:bg-danger-900/25"
                            >
                              <MdDelete className="h-4 w-4" />
                            </button>
                          ))}
                        <ActionMenu
                          user={user}
                          canSetPassword={canSetPassword}
                          canToggle={canToggle}
                          onSetPassword={onSetPassword}
                          onToggleActive={onToggleActive}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer: row count only — no pagination controls */}
      {!loading && safeData.length > 0 && (
        <div className="mt-4 flex shrink-0 items-center justify-end border-t border-line pt-3 text-xs text-ink-muted">
          {footerBefore}
          <strong className="mx-1 text-ink-muted">
            {totalUsers.toLocaleString(locale)}
          </strong>
          {footerAfter}
        </div>
      )}
    </Card>
  );
}

export default UserTable;
